import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { imageStyles } from "@insite/content-library/Widgets/BrandDetails/BrandDetailsImage";
import { logoStyles } from "@insite/content-library/Widgets/BrandDetails/BrandDetailsLogo";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};
interface OwnProps extends PageProps {}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    logoWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
    mainNavigationWrapper?: InjectableCss;
    headerLinksWrapper?: InjectableCss;
    scrollFixed?: InjectableCss;
    pageContainer?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    container: {
        css: css`
            .navigationLink {
                span[class^="IconWrapper"] {
                    margin: -3px 0px 0px 2px;

                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                null,
                                null,
                                null,
                                css`
                                    margin: -1px 0px 0px 2px;
                                    width: 12px;
                                `,
                                null,
                            ],
                            "max",
                        );
                    }}
                }
            }
            div[data-test-selector="brandImage"] {
                div[class^="GridItemStyle"] {
                    h3 {
                        display: none;
                    }
                }
                > div[class^="GridOffset"] {
                    > div[class^="GridItemStyle"] {
                        > div[class^="GridWrapper"] {
                            > div[class^="GridOffset"] {
                                > div[class^="GridItemStyle"] {
                                    justify-content: flex-end;
                                }
                            }
                        }
                    }
                }
            }
            display: flex;
            max-width: 1440px;
            margin: 0 auto;
            padding: 0 17.5px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            margin-bottom: 15px;
                            position: relative;
                            padding: 0 17.5px;
                        `,
                        css`
                            flex-direction: column;
                            margin-bottom: 15px;
                            position: relative;
                            padding: 0 17.5px;
                        `,
                        css`
                            flex-direction: column;
                            margin-bottom: 15px;
                            position: relative;
                            padding: 0 17.5px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    websiteName: {
        css: css`
            ~ header[class^="PageStyle"] {
                & + div[class^="PageStyle"] {
                    nav[data-test-selector="pageBreadcrumbs"] {
                        span {
                            text-transform: capitalize;
                            display: initial !important;
                        }
                        span[aria-current="page"] {
                            max-width: 100px;
                            white-space: nowrap;
                        }

                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    null,
                                    null,
                                    null,
                                    css`
                                        white-space: nowrap;
                                        display: block;
                                        overflow: hidden;
                                        overflow-x: auto;
                                        padding-bottom: 12px;
                                    `,
                                    null,
                                ],
                                "max",
                            )}
                    }
                }
            }

            ~ main {
                min-height: 550px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            min-height: 800px;
                        `,
                        css`
                            min-height: 1100px;
                        `,
                    ])}
            }

            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    logoWrapper: {
        css: css`
            flex-basis: 16%;
            padding-bottom: 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-basis: 100%;
                            text-align: center;
                            margin-top: 10px;
                        `,
                        css`
                            flex-basis: 100%;
                            text-align: center;
                            margin-top: 10px;
                        `,
                        css`
                            flex-basis: 100%;
                            text-align: center;
                            margin-top: 10px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            & img {
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                height: 50px;
                                width: auto;
                            `,
                            css`
                                height: 50px;
                                width: auto;
                            `,
                            css`
                                height: 50px;
                                width: auto;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
    searchWrapper: {
        css: css`
            flex-basis: 33%;
            display: flex;
            align-items: center;

            & div[class^="PopoverStyle"] {
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                width: calc(100% - 35px);
                                left: auto !important;
                            `,
                            css`
                                width: calc(100% - 35px);
                                left: auto !important;
                            `,
                            css`
                                width: calc(100% - 35px);
                                left: auto !important;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
    mainNavigationWrapper: {
        css: css`
            flex-basis: 60%;
            display: flex;
            align-items: center;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            flex-basis: 45%;
                        `,
                        null,
                    ],
                    "max",
                );
            }}
            & div[class^="Wrapper"] {
                display: flex;
                height: 100%;
            }
            div[class^="HiddenStyle"] {
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                width: 96%;
                            `,
                            null,
                        ],
                        "max",
                    );
                }}
                span {
                    font-size: 14px;
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                null,
                                null,
                                null,
                                css`
                                    font-size: 9px;
                                `,
                                null,
                            ],
                            "max",
                        );
                    }}
                }
            }
        `,
    },
    headerLinksWrapper: {
        css: css`
            flex-basis: 18%;
            display: flex;
            align-items: center;
            a[data-test-selector="cartLink"] {
                height: 100%;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            position: absolute;
                            right: 10px;
                            top: 22px;
                        `,
                        css`
                            position: absolute;
                            right: 10px;
                            top: 22px;
                        `,
                        css`
                            position: absolute;
                            right: 10px;
                            top: 22px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            & > div a span[class^="LazyImageStyle"] {
                padding: 0 10px 0 10px;
                border-radius: 20px;
                width: 40px;
                height: 32px;
                margin: 0px;
            }
            & div.contactLinkWithIcon > a span[class^="LazyImageStyle"] {
                height: 22px;
            }
            & div.contactLinkWithIcon > a span[class^="TypographyStyle"] {
                display: flex;
                flex-direction: column;
                align-items: center;
            }
        `,
    },
    scrollFixed: {
        css: css`
            z-index: 100;
            position: fixed;
            top: 0;
            border-bottom: 5px solid ${getColor("primary")};
            * {
                transition: all 0.4s ease;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-top: 0px;
                            max-width: 100%;
                            border-bottom: none;
                        `,
                        css`
                            padding-top: 0px;
                            max-width: 100%;
                            border-bottom: 5px solid #0000ff;
                        `,
                        css`
                            padding-top: 0px;
                            max-width: 100%;
                            border-bottom: 5px solid #0000ff;
                        `,
                        css`
                            padding-top: 0px;
                            max-width: 100%;
                            border-bottom: 5px solid #0000ff;
                        `,
                        css`
                            padding-top: 0px;
                            max-width: 100%;
                            border-bottom: 5px solid #0000ff;
                        `,
                    ],
                    "min",
                );
            }}
            & .ticker {
                display: none;
            }
            & .navigationLink {
                &:nth-child(1) {
                    display: block;
                }
            }
            & .headerLinksWrapper {
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                position: absolute;
                                right: 10px;
                                top: 22px;
                            `,
                            css`
                                position: absolute;
                                right: 10px;
                                top: 22px;
                            `,
                            css`
                                position: absolute;
                                right: 10px;
                                top: 22px;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
            & .megaMenuDrawerWrapper {
                top: 64px;
                > div:first-child {
                    overflow-y: auto;
                    height: auto;
                    max-height: 80vh;
                }

                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                top: 65px;
                            `,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
    pageContainer: {
        css: css`
            position: relative;
            z-index: 10;
            box-shadow: 0px 3px 6px #00000017;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            box-shadow: none;
                        `,
                        css`
                            box-shadow: none;
                        `,
                        css`
                            box-shadow: none;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
};
// BrandDetailsPage
imageStyles.productLinkItem = {
    width: 12,
    css: css`
        justify-content: flex-end;
        text-align: center;
        padding-right: 20px;
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        justify-content: center;
                    `,
                    null,
                    null,
                ],
                "max",
            )}
        a[data-test-selector="brandToProductPageLink"] {
            padding: 7px 21px 5px;
            background: #91a2a4;
            border: 2px solid #91a2a4;
            color: rgb(255, 255, 255);
            display: flex;
            align-items: center;
            justify-content: center;
            &:hover {
                background-color: ${getColor("primary.main")};
            }
            span {
                color: #fff !important;
                text-transform: uppercase;
            }
        }
    `,
};
imageStyles.imageMissingText = {
    variant: "h3",
    css: css`
        width: 100%;
        overflow-wrap: break-word;
        word-wrap: break-word;
        display: none;
    `,
};

logoStyles.container = {
    css: css`
        max-width: 300px;
        margin: 15px;
    `,
};

const styles = headerStyles;

const Header: React.FC<Props> = ({ id, siteName, session, toaster, history, myAccountPageUrl, dismissRfqUpdates }) => {
    const afterHeader = React.createRef<HTMLSpanElement>();
    const [offset, setOffset] = useState(0);
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };
    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
        window.onscroll = () => {
            const check = window.parent.document.getElementById("siteIFrame");
            if (!check) {
                setOffset(window.pageYOffset);
            } else {
                setOffset(1);
            }
        };
    }, [myAccountPageUrl]);
    return (
        <>
            {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>}
            <Page
                as="header"
                isHeader={true}
                fullWidth={[true, true, true, true, true]}
                {...styles.pageContainer}
                {...(offset > 40 ? { ...styles.scrollFixed } : "")}
            >
                {/* <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                /> */}
                <StyledWrapper className="ticker">
                    <Zone contentId={id} zoneName="Ticker" />
                </StyledWrapper>
                <StyledWrapper {...styles.container}>
                    <StyledWrapper {...styles.logoWrapper}>
                        <Zone contentId={id} zoneName="Logo" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.mainNavigationWrapper} className="mainNavigationWrapper">
                        <Zone contentId={id} zoneName="Search" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.searchWrapper}>
                        <Zone contentId={id} zoneName="MainNavigation" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.headerLinksWrapper} className="headerLinksWrapper">
                        <Zone contentId={id} zoneName="HeaderLinks" />
                    </StyledWrapper>
                </StyledWrapper>
                <Zone contentId={id} zoneName="Breadcrumb" />
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
